import React from 'react';
import { alpha, Box, Card, Grid, Typography, LinearProgress, Stack } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useTranslation } from 'react-i18next';
import { getTierRatePercent, getTierStyle, tierLabel } from '../../tiers';

// Keeps null/undefined as null (Gold sends pointsToNextTier === null) and collapses anything
// non-numeric to null, so no rendered number can ever become NaN.
const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const eur = (cents) => ((toNumber(cents) ?? 0) / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const labelSx = { display: 'block', opacity: 0.75, letterSpacing: '0.1em', lineHeight: 1.6 };

const HeroStatusCard = ({ data }) => {
    const { t } = useTranslation();
    const { tier, qualifyingPoints, pointsToNextTier, nextTierName, currentCreditAmountCents } = data;
    const tierName = tier ? tier.name : null;
    const ratePercent = getTierRatePercent(tierName);

    // Gold sends pointsToNextTier === null; any other incomplete "next tier" payload is treated the
    // same way so the progress maths can never divide by zero or render NaN.
    const points = Math.max(0, toNumber(qualifyingPoints) ?? 0);
    const nextTierPoints = toNumber(pointsToNextTier);
    const pointsToNext = nextTierPoints === null ? null : Math.max(0, nextTierPoints);
    const atTop = pointsToNext === null || !nextTierName;
    const total = points + (pointsToNext ?? 0);
    const progress = atTop || total === 0 ? 100 : Math.min(100, (points / total) * 100);

    return (
        <Card
            elevation={0}
            sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 3, p: { xs: 3, md: 4 } }}
        >
            <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <Typography variant="overline" sx={labelSx}>
                        {t('loyalty.hero.status')}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                        <WorkspacePremiumIcon
                            sx={{ color: getTierStyle(tierName).bgcolor, fontSize: 32, flexShrink: 0 }}
                        />
                        <Typography variant="h4" sx={{ fontSize: '2rem', lineHeight: 1.1 }}>
                            {tierLabel(t, tierName)}
                        </Typography>
                    </Stack>
                    {ratePercent !== null && (
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
                            {t('loyalty.hero.tierRate', { rate: ratePercent })}
                        </Typography>
                    )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <Typography variant="overline" sx={labelSx}>
                        {t('loyalty.hero.points')}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                        <Typography variant="h3" sx={{ fontSize: '2.5rem', lineHeight: 1.1 }}>
                            {points}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                            {t('loyalty.hero.pointsUnit')}
                        </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
                        {atTop ? (
                            <>
                                {t('loyalty.hero.topTier')}{' '}
                                <Box component="span" aria-hidden="true">
                                    🎉
                                </Box>
                            </>
                        ) : (
                            t('loyalty.hero.toNext', { points: pointsToNext, tier: tierLabel(t, nextTierName) })
                        )}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <Typography variant="overline" sx={labelSx}>
                        {t('loyalty.hero.credit')}
                    </Typography>
                    <Typography variant="h3" sx={{ fontSize: '2.125rem', lineHeight: 1.1 }}>
                        {eur(currentCreditAmountCents)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
                        {t('loyalty.hero.creditHint')}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                    <Typography variant="overline" sx={labelSx}>
                        {t('loyalty.hero.progressLabel')}
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mb: 1, justifyContent: 'space-between', alignItems: 'baseline' }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tierLabel(t, tierName)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'right' }}>
                            {atTop ? t('loyalty.hero.progressMax') : tierLabel(t, nextTierName)}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            bgcolor: (theme) => alpha(theme.palette.common.white, 0.25),
                            '& .MuiLinearProgress-bar': { bgcolor: 'secondary.main', borderRadius: 5 },
                        }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.85 }}>
                        {t('loyalty.hero.progressReached', { percent: Math.round(progress) })}
                    </Typography>
                </Grid>
            </Grid>
        </Card>
    );
};

export default HeroStatusCard;
